
import { Injectable } from '@angular/core';
import { SalesSummary, ChartDataPoint, Kpi, DailySalesData, AccountingSummary, BestsellerItem, SalesSummaryReport, CategorySalesReport, EmployeeSalesReport, PaymentMethodSalesReport, DiscountReport, TaxReport, ShiftReport, TimeClockEntry, HoursWorkedSummary, ClinicalSymptomStats, DoctorPerformanceStats, ReportPht27Item, ReportPht28Item, FinancialReport, ReferralStatItem } from '../models/index';
import { getSupabase, isSupabaseConfigured } from '../firebase';
import { SupabaseClient } from '@supabase/supabase-js';

@Injectable({ providedIn: 'root' })
export class ReportingService {
  private supabase: SupabaseClient | null = getSupabase();

  // ... (Previous methods: getSalesSummaryData, etc. keep as is) ...
  async getSalesSummaryData(startDate: string, endDate: string): Promise<SalesSummary> {
    if (!isSupabaseConfigured() || !this.supabase) {
      // Mock Data adjusted for "Clinic" Feel
      return {
        kpis: [
          { title: 'backOffice.dashboard.kpi.netSales', prefix: '฿', value: 43098.50, changeValue: 873.00, changePercent: 0.17 },
          { title: 'New Patients', value: 12, changeValue: 2, changePercent: 20 },
          { title: 'Pending Prescriptions', value: 5, changeValue: 0, changePercent: 0 },
          { title: 'Approved Prescriptions', value: 28, changeValue: 5, changePercent: 21 },
        ],
        salesOverTime: [
            { date: '2023-10-01', value: 5000 },
            { date: '2023-10-02', value: 7200 },
            { date: '2023-10-03', value: 4800 },
            { date: '2023-10-04', value: 6100 },
        ], 
        dailyBreakdown: []
      };
    }

    const { data, error } = await this.supabase.rpc('get_dashboard_summary', { 
        p_start_date: startDate,
        p_end_date: endDate
    });

    if (error) {
      console.error('Error fetching dashboard summary:', error);
      throw error;
    }

    const kpis: Kpi[] = [
        { title: 'backOffice.dashboard.kpi.netSales', prefix: '฿', value: data.kpis.net_sales, changeValue: 0, changePercent: 0 },
        { title: 'New Patients', value: data.kpis.new_patients, changeValue: 0, changePercent: 0 },
        { title: 'Pending Prescriptions', value: data.kpis.prescriptions_pending, changeValue: 0, changePercent: 0 },
        { title: 'Approved Prescriptions', value: data.kpis.prescriptions_approved, changeValue: 0, changePercent: 0 },
    ];

    const dailyBreakdown: DailySalesData[] = (data.daily_breakdown || []).map((d: any) => ({
        date: d.date || d.day, 
        grossSales: d.grossSales,
        netSales: d.netSales,
    }));

    const salesOverTime: ChartDataPoint[] = dailyBreakdown.map(d => ({
        date: d.date ? String(d.date).split('T')[0] : '', // Safely handle date string
        value: d.netSales, 
    }));

    return { kpis, dailyBreakdown, salesOverTime };
  }
  
  async getAccountingReport(startDate: string, endDate: string): Promise<AccountingSummary> {
    const mockRevenueBreakdown = [
        { type: 'Medical' as const, amount: 154000, percentage: 65 }, 
        { type: 'Service' as const, amount: 45000, percentage: 20 }, 
        { type: 'Goods' as const, amount: 35000, percentage: 15 }
    ];

    if (!isSupabaseConfigured() || !this.supabase) {
      return {
        kpis: [
          { title: 'Total Revenue', prefix: '฿', value: 234000, changeValue: 12000, changePercent: 5.4 },
          { title: 'Cost of Goods (COGS)', prefix: '฿', value: 85000, changeValue: 0, changePercent: 0 },
          { title: 'Gross Profit', prefix: '฿', value: 149000, changeValue: 0, changePercent: 0 },
          { title: 'Loyalty Liability', value: 12500, prefix: 'Pts', changeValue: 0, changePercent: 0 },
        ],
        redemptions: [],
        revenueBreakdown: mockRevenueBreakdown
      };
    }

    const { data, error } = await this.supabase.rpc('get_accounting_report', {
      p_start_date: startDate,
      p_end_date: endDate
    });

    if (error) {
      console.error('Error fetching accounting report:', error);
      throw error;
    }

    return {
        ...data,
        revenueBreakdown: mockRevenueBreakdown
    } as AccountingSummary;
  }

  // NEW: Get Financial Report (Split Clinic vs Retail)
  async getFinancialReport(startDate: string, endDate: string): Promise<FinancialReport> {
    if (!isSupabaseConfigured() || !this.supabase) {
        // Mock data
        return {
            clinic_revenue: 150000,
            retail_revenue: 50000,
            total_revenue: 200000,
            cogs: 80000,
            expenses: 20000,
            net_profit: 100000,
            top_clinic_items: [{ name: 'Blue Dream', sales: 50000 }, { name: 'Doctor Consult', sales: 30000 }],
            top_retail_items: [{ name: 'Rolling Paper', sales: 5000 }, { name: 'T-Shirt', sales: 4000 }]
        };
    }
    
    const { data, error } = await this.supabase.rpc('get_financial_report', {
        p_start_date: startDate,
        p_end_date: endDate
    });

    if (error) throw error;
    return data as FinancialReport;
  }
  
  // NEW: Get Referral Stats
  async getClinicReferralStats(startDate: string, endDate: string): Promise<ReferralStatItem[]> {
      if (!isSupabaseConfigured() || !this.supabase) {
          // Mock
          return [
              { partnerName: 'ร้านกัญชาอโศก (Asoke)', partnerId: '1', totalRequests: 15, approvedCount: 12, rejectedCount: 1, pendingCount: 2, lastActivity: new Date().toISOString() },
              { partnerName: 'Siam Dispensary', partnerId: '2', totalRequests: 8, approvedCount: 8, rejectedCount: 0, pendingCount: 0, lastActivity: new Date(Date.now() - 86400000).toISOString() }
          ];
      }
      
      const { data, error } = await this.supabase.rpc('get_clinic_referral_stats', {
          p_start_date: startDate,
          p_end_date: endDate
      });
      
      if (error) throw error;
      return data as ReferralStatItem[];
  }

  // ... (Other existing methods: getBestsellersReport, getSalesSummaryReport, etc.) ...
  async getBestsellersReport(startDate: string, endDate: string, cashierId?: string): Promise<BestsellerItem[]> {
    if (!isSupabaseConfigured() || !this.supabase) {
      return Promise.resolve([
        { productId: 'PROD001', productName: '(100G) Tiger Eye', sku: '10154', category: 'FLOWER', quantitySold: 55.5, grossSales: 24975.00 },
        { productId: 'PROD012', productName: 'CBD Oil Tincture', sku: '20101', category: 'EDIBLE', quantitySold: 20, grossSales: 24000.00 },
      ]);
    }

    const { data, error } = await this.supabase.rpc('get_bestsellers_report', { 
        p_start_date: startDate,
        p_end_date: endDate,
        p_cashier_id: cashierId
    });

    if (error) {
      console.error('Error fetching bestsellers report:', error);
      throw error;
    }

    return data as BestsellerItem[];
  }

  async getSalesSummaryReport(startDate: string, endDate: string, cashierId?: string): Promise<SalesSummaryReport> {
    if (!this.supabase) return { grossSales: 0, netSales: 0, discounts: 0, transactionCount: 0, averageSale: 0 };
    const { data, error } = await this.supabase.rpc('get_sales_summary_report', { 
        p_start_date: startDate, p_end_date: endDate, p_cashier_id: cashierId
    });
    if (error) throw error;
    return data;
  }

  async getCategorySalesReport(startDate: string, endDate: string, cashierId?: string): Promise<CategorySalesReport[]> {
    if (!this.supabase) return [];
    const { data, error } = await this.supabase.rpc('get_sales_by_category_report', {
        p_start_date: startDate, p_end_date: endDate, p_cashier_id: cashierId
    });
    if (error) throw error;
    return data;
  }
  
  async getEmployeeSalesReport(startDate: string, endDate: string, cashierId?: string): Promise<EmployeeSalesReport[]> {
    if (!this.supabase) return [];
    const { data, error } = await this.supabase.rpc('get_sales_by_employee_report', {
        p_start_date: startDate, p_end_date: endDate, p_cashier_id: cashierId
    });
    if (error) throw error;
    return data;
  }

  async getPaymentMethodSalesReport(startDate: string, endDate: string, cashierId?: string): Promise<PaymentMethodSalesReport[]> {
    if (!this.supabase) return [];
    const { data, error } = await this.supabase.rpc('get_sales_by_payment_method_report', {
        p_start_date: startDate, p_end_date: endDate, p_cashier_id: cashierId
    });
    if (error) throw error;
    return data;
  }

  async getDiscountsReport(startDate: string, endDate: string, cashierId?: string): Promise<DiscountReport[]> {
    if (!this.supabase) return [];
    const { data, error } = await this.supabase.rpc('get_discounts_report', {
        p_start_date: startDate, p_end_date: endDate, p_cashier_id: cashierId
    });
    if (error) throw error;
    return data;
  }
  
  async getTaxesReport(startDate: string, endDate: string, cashierId?: string): Promise<TaxReport[]> {
    if (!this.supabase) return [];
    const { data, error } = await this.supabase.rpc('get_taxes_report', {
        p_start_date: startDate, p_end_date: endDate, p_cashier_id: cashierId
    });
    if (error) throw error;
    return data;
  }

  async getShiftsReport(startDate: string, endDate: string, cashierId?: string): Promise<ShiftReport[]> {
    if (!this.supabase) return [];
    const { data, error } = await this.supabase.rpc('get_shifts_report', {
        p_start_date: startDate, p_end_date: endDate, p_cashier_id: cashierId
    });
    if (error) throw error;
    return data;
  }

  async getClinicalSymptomStats(startDate: string, endDate: string): Promise<ClinicalSymptomStats[]> {
    if (!isSupabaseConfigured() || !this.supabase) {
        return [
          { symptom: 'นอนไม่หลับ (Insomnia)', count: 45, percentage: 35 },
          { symptom: 'ปวดเรื้อรัง (Chronic Pain)', count: 30, percentage: 23 },
          { symptom: 'เครียด (Stress)', count: 25, percentage: 19 },
          { symptom: 'ไมเกรน (Migraine)', count: 15, percentage: 11 },
          { symptom: 'ออฟฟิศซินโดรม (Office Syndrome)', count: 15, percentage: 12 },
        ];
    }
    const { data, error } = await this.supabase.rpc('get_clinical_symptom_stats', {
        p_start_date: startDate,
        p_end_date: endDate
    });
    if (error) throw error;
    return data;
  }

  async getDoctorPerformance(startDate: string, endDate: string): Promise<DoctorPerformanceStats[]> {
    if (!isSupabaseConfigured() || !this.supabase) {
        return [
            { doctorName: 'Dr. Aris', patientCount: 120, prescriptionsIssued: 115 },
            { doctorName: 'Dr. Somchai', patientCount: 85, prescriptionsIssued: 80 },
        ];
    }
    const { data, error } = await this.supabase.rpc('get_doctor_performance', {
        p_start_date: startDate,
        p_end_date: endDate
    });
    if (error) throw error;
    return data;
  }

  async getTimeClockReport(startDate: string, endDate: string, cashierId?: string): Promise<TimeClockEntry[]> {
    if (!this.supabase) return [];
    const { data, error } = await this.supabase.rpc('get_time_clock_report', {
        p_start_date: startDate, p_end_date: endDate, p_cashier_id: cashierId
    });
    if (error) throw error;
    return data;
  }

  async getHoursWorkedSummary(startDate: string, endDate: string, cashierId?: string): Promise<HoursWorkedSummary[]> {
    if (!this.supabase) return [];
    const { data, error } = await this.supabase.rpc('get_hours_worked_summary', {
        p_start_date: startDate, p_end_date: endDate, p_cashier_id: cashierId
    });
    if (error) throw error;
    return data;
  }

  async getPht27Report(month: string): Promise<ReportPht27Item[]> {
    if (!this.supabase) return [];
    const { data, error } = await this.supabase.rpc('get_report_pht27', { p_month: month });
    if (error) throw error;
    return (data || []).map((item: any) => ({
        id: item.id,
        dateReceived: item.date_received,
        licenseNumber: item.license_number,
        supplierName: item.supplier_name,
        description: item.description,
        quantity: item.quantity,
        accumulatedTotal: item.accumulated_total, // NEW: Map accumulated total
        unit: item.unit,
        cultivationArea: item.cultivation_area
    }));
  }

  async getPht28Report(month: string): Promise<ReportPht28Item[]> {
    if (!this.supabase) return [];
    const { data, error } = await this.supabase.rpc('get_report_pht28', { p_month: month });
    if (error) throw error;
    return (data || []).map((item: any) => ({
        transactionDate: item.transaction_date,
        buyerId: item.buyer_id,
        buyerName: item.buyer_name,
        buyerBirthDate: item.buyer_birth_date,
        productName: item.product_name,
        quantity: item.quantity,
        unit: item.unit
    }));
  }

  async batchSavePht27(entries: ReportPht27Item[]): Promise<void> {
    if (!this.supabase) throw new Error('Database not connected.');
    const payload = entries.map(e => ({
        date_received: e.dateReceived,
        license_number: e.licenseNumber,
        supplier_name: e.supplierName,
        description: e.description,
        quantity: e.quantity,
        cultivation_area: e.cultivationArea,
    }));
    const { error } = await this.supabase.rpc('batch_save_pht27_entries', { entries: payload });
    if (error) throw error;
  }

  async updatePht27Entry(id: string, entry: ReportPht27Item): Promise<void> {
    if (!this.supabase) throw new Error('Database not connected.');
    const payload = {
        date_received: entry.dateReceived,
        license_number: entry.licenseNumber,
        supplier_name: entry.supplierName,
        description: entry.description,
        quantity: entry.quantity,
        cultivation_area: entry.cultivationArea,
    };
    const { error } = await this.supabase.rpc('update_pht27_entry', { p_entry_id: id, p_new_data: payload });
    if (error) throw error;
  }

  async deletePht27Entry(id: string): Promise<void> {
    if (!this.supabase) throw new Error('Database not connected.');
    const { error } = await this.supabase.rpc('delete_pht27_entry', { p_entry_id: id });
    if (error) throw error;
  }
}
